import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Grid,
    Card,
    CardContent,
    Typography,
    Paper,
    CircularProgress,
    Alert,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Switch,
    FormControlLabel,
    useMediaQuery,
    useTheme,
    Divider
} from '@mui/material';
import {
    Person,
    Business,
    Subscriptions,
    TrendingUp,
    CheckCircle,
    Warning,
    Error,
    PlayArrow as ExecuteIcon,
    Settings as SettingsIcon,
    Create as CreateIcon,
    Image as ImageIcon,
    Article as ArticleIcon,
    Web as WebIcon,
    Refresh as RefreshIcon,
    Delete as DeleteIcon,
    Analytics as AnalyticsIcon,
    AutoAwesome as AutoAwesomeIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import axiosWithAuth from '../utils/axiosWithAuth';
import { isCASOnlySubscription } from '../utils/subscriptionUtils';

const DashboardPage = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [loading, setLoading] = useState(true);
    const [subscriptions, setSubscriptions] = useState([]);
    const [usage, setUsage] = useState({});
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const hasAnyAWCSubscription = useMemo(() => {
        try {
            return Array.isArray(subscriptions) && subscriptions.some(sub => !isCASOnlySubscription(sub));
        } catch {
            return false;
        }
    }, [subscriptions]);


    // Operation state
    const [operationDialog, setOperationDialog] = useState(false);
    const [selectedOperation, setSelectedOperation] = useState('');
    const [selectedSubscription, setSelectedSubscription] = useState(null);
    const [operationForm, setOperationForm] = useState({
        requested_pages: '',
        requested_blog_topics: '',
        image_generation_required: true,
        use_default_placeholder_images: false,
        include_default_pages: true,
        generate_blogs: true
    });
    const [executing, setExecuting] = useState(false);

    // Aggregate usage across subscriptions to power Usage Overview when backend overview lacks fields
    const computeAggregatedUsage = (subs, backendOverview) => {
        try {
            const safeSubs = Array.isArray(subs) ? subs : [];
            const totals = safeSubs.reduce(
                (acc, sub) => {
                    const contentTotal = sub?.content_stats?.total_content || 0;
                    const imagesTotal = sub?.image_stats?.total_images || 0;
                    const blogsTotal = sub?.blog_stats?.total_blogs || 0;
                    const quotaUsed = sub?.quota_used || 0;
                    const quotaLimit = sub?.quota_limit || 0;

                    acc.content_generation += contentTotal;
                    acc.image_generation += imagesTotal;
                    acc.blog_generation += blogsTotal;
                    acc.total_quota_used += quotaUsed;
                    acc.total_quota_limit += quotaLimit;
                    return acc;
                },
                {
                    content_generation: 0,
                    image_generation: 0,
                    blog_generation: 0,
                    total_quota_used: 0,
                    total_quota_limit: 0
                }
            );

            const derived = {
                content_generation: totals.content_generation,
                image_generation: totals.image_generation,
                blog_generation: totals.blog_generation,
                // We do not have per-feature limits; show aggregate plan limit for context
                content_limit: totals.total_quota_limit,
                image_limit: totals.total_quota_limit,
                blog_limit: totals.total_quota_limit,
                total_usage: totals.total_quota_limit > 0
                    ? Math.round((totals.total_quota_used / totals.total_quota_limit) * 100)
                    : 0
            };

            // Merge any useful fields from backend overview (if present)
            return {
                ...(backendOverview || {}),
                ...derived
            };
        } catch (e) {
            return backendOverview || {};
        }
    };

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            const [subscriptionsRes, usageRes] = await Promise.all([
                axiosWithAuth.get('/subscriptions/my-subscriptions'),
                axiosWithAuth.get('/subscriptions/stats/overview')
            ]);

            const subs = subscriptionsRes.data || [];
            const overview = usageRes.data || {};

            setSubscriptions(subs);
            setUsage(computeAggregatedUsage(subs, overview));
        } catch (error) {
            setError('Failed to fetch dashboard data: ' + (error.response?.data?.detail || error.message));
            // Set default values to prevent filter errors
            setSubscriptions([]);
            setUsage({});
        } finally {
            setLoading(false);
        }
    };


    const getStatusIcon = (status) => {
        switch (status) {
            case 'active':
                return <CheckCircle color="success" />;
            case 'cancelled':
                return <Error color="error" />;
            case 'suspended':
                return <Warning color="warning" />;
            default:
                return <Warning color="warning" />;
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'active':
                return 'success';
            case 'cancelled':
                return 'error';
            case 'suspended':
                return 'warning';
            default:
                return 'default';
        }
    };

    // Mobile-friendly subscription card component
    const SubscriptionCard = ({ subscription }) => (
        <Card sx={{ mb: 2, maxWidth: '100%', overflow: 'hidden' }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2} flexWrap="wrap" gap={1}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="h6" fontWeight="bold" sx={{ wordBreak: 'break-word', overflow: 'hidden' }}>
                            {subscription.subscription_name || subscription.plan?.name || 'Unnamed Subscription'}
                        </Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ wordBreak: 'break-word' }}>
                            {subscription.subscription_name ?
                                `Plan: ${subscription.plan?.name || 'Unknown Plan'} - $${subscription.plan?.price_monthly || 0}/month` :
                                `$${subscription.plan?.price_monthly || 0}/month`
                            }
                        </Typography>
                    </Box>
                    <Chip
                        icon={getStatusIcon(subscription.status)}
                        label={subscription.status}
                        color={getStatusColor(subscription.status)}
                        size="small"
                        sx={{ flexShrink: 0 }}
                    />
                </Box>

                <Grid container spacing={1} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="textSecondary">Quota Usage</Typography>
                        {isCASOnlySubscription(subscription) ? (
                            <Typography variant="body2" color="textSecondary">
                                CAS Access Only
                            </Typography>
                        ) : (
                            <Typography variant="body2" fontWeight="bold">
                                {subscription.quota_used || 0} / {subscription.quota_limit || 0}
                            </Typography>
                        )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="textSecondary">Content Generated</Typography>
                        {isCASOnlySubscription(subscription) ? (
                            <Typography variant="body2" color="textSecondary">
                                CAS Content
                            </Typography>
                        ) : (
                            <Typography variant="body2" fontWeight="bold">
                                {subscription.content_stats?.total_content || 0} pieces
                            </Typography>
                        )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="textSecondary">Images Created</Typography>
                        {isCASOnlySubscription(subscription) ? (
                            <Typography variant="body2" color="textSecondary">
                                CAS Images
                            </Typography>
                        ) : (
                            <Typography variant="body2" fontWeight="bold">
                                {subscription.image_stats?.total_images || 0} images
                            </Typography>
                        )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="textSecondary">Blogs Published</Typography>
                        {isCASOnlySubscription(subscription) ? (
                            <Typography variant="body2" color="textSecondary">
                                CAS Blogs
                            </Typography>
                        ) : (
                            <Typography variant="body2" fontWeight="bold">
                                {subscription.blog_stats?.total_blogs || 0} blogs
                            </Typography>
                        )}
                    </Grid>
                </Grid>

                <Divider sx={{ my: 1 }} />

                <Box display="flex" gap={1} flexWrap="wrap" justifyContent="center" sx={{ maxWidth: '100%' }}>
                    <Tooltip title="Content Analysis Service (AI-driven content generation with analysis)">
                        <IconButton
                            size="medium"
                            sx={{
                                backgroundColor: 'secondary.light',
                                color: 'secondary.contrastText',
                                '&:hover': {
                                    backgroundColor: 'secondary.main',
                                }
                            }}
                            onClick={() => navigate(`/cas?subscriptionId=${subscription.id}`)}
                        >
                            <AnalyticsIcon />
                        </IconButton>
                    </Tooltip>

                    {!isCASOnlySubscription(subscription) && (
                        <Tooltip title="Automated Website Creation (User and AI driven content generation)">
                            <IconButton
                                size="medium"
                                sx={{
                                    backgroundColor: 'info.light',
                                    color: 'info.contrastText',
                                    '&:hover': {
                                        backgroundColor: 'info.main',
                                    }
                                }}
                                onClick={() => navigate(`/service-operations?subscriptionId=${subscription.id}`)}
                            >
                                <AutoAwesomeIcon />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            </CardContent>
        </Card>
    );

    // Operation handling
    const openOperationDialog = (subscription, operationType) => {
        setSelectedSubscription(subscription);
        setSelectedOperation(operationType);
        setOperationForm({
            requested_pages: '',
            requested_blog_topics: '',
            image_generation_required: true,
            use_default_placeholder_images: false,
            include_default_pages: true,
            generate_blogs: true
        });
        setOperationDialog(true);
    };

    const handleTriggerOperation = async () => {
        try {
            setExecuting(true);
            const subscriptionId = selectedSubscription.id;

            // Determine operation type
            let operationType;
            switch (selectedOperation) {
                case 'images':
                    operationType = 'generate_images';
                    break;
                case 'blogs':
                    operationType = 'generate_blogs';
                    break;
                case 'pages':
                    operationType = 'generate_pages';
                    break;
                case 'website':
                    operationType = 'create_website';
                    break;
                default:
                    throw new Error('Invalid operation type');
            }

            // Prepare parameters
            const parameters = {
                subscription_id: subscriptionId,
                ...operationForm,
                requested_pages: operationForm.requested_pages.split(',').map(s => s.trim()).filter(s => s),
                requested_blog_topics: operationForm.requested_blog_topics.split('\n').map(s => s.trim()).filter(s => s)
            };

            // Set dump_content_files based on operation type per configuration matrix
            if (['generate_images', 'generate_blogs', 'generate_pages'].includes(operationType)) {
                parameters.dump_content_files = true;  // No WordPress site - content/images only
            } else {
                parameters.dump_content_files = false; // WordPress site creation
            }

            // For image-only generation, ensure correct flags
            if (operationType === 'generate_images') {
                // Add required flags for image-only generation
                parameters.page_generation_mode = 'custom';
                parameters.blog_generation_mode = 'custom';
                parameters.requested_pages = [];
                parameters.requested_blog_topics = [];
            }

            // Convert image prompts to proper format for any operation that uses images (only if provided)
            if (operationForm.requested_image_prompts && operationForm.requested_image_prompts.trim() && ['generate_images', 'generate_pages', 'generate_blogs', 'create_website'].includes(operationType)) {
                const imagePrompts = operationForm.requested_image_prompts
                    .split('\n')
                    .map(line => line.trim())
                    .filter(line => line && line.includes(':'))
                    .map(line => {
                        const [identifier, ...promptParts] = line.split(':');
                        const prompt = promptParts.join(':').trim();
                        return {
                            identifier: identifier.trim(),
                            prompt: prompt,
                            style: operationForm.image_art_style || 'photorealistic'
                        };
                    });

                if (imagePrompts.length > 0) {
                    parameters.requested_image_prompts = imagePrompts;
                } else {
                    // Fallback: create default identifiers
                    const fallbackPrompts = operationForm.requested_image_prompts
                        .split('\n')
                        .map(line => line.trim())
                        .filter(line => line)
                        .map((prompt, index) => ({
                            identifier: `image_${index + 1}`,
                            prompt: prompt,
                            style: operationForm.image_art_style || 'photorealistic'
                        }));
                    parameters.requested_image_prompts = fallbackPrompts;
                }
            }

            const response = await axiosWithAuth.post(`/external/operations/${operationType}`, parameters);
            setSuccess(`Operation started successfully! Task ID: ${response.data.task_id}`);
            setOperationDialog(false);
            fetchDashboardData(); // Refresh data
        } catch (error) {
            setError('Failed to trigger operation: ' + (error.response?.data?.detail || error.message));
        } finally {
            setExecuting(false);
        }
    };



    const stats = [
        {
            title: 'Active Subscriptions',
            value: (Array.isArray(subscriptions) ? subscriptions.filter(sub => sub.status === 'active').length : 0).toString(),
            icon: <Subscriptions color="primary" />,
            color: '#1976d2',
            tooltip: 'Number of currently active subscriptions'
        },
        {
            title: 'Quota Usage',
            value: `${usage.total_usage || 0}%`,
            icon: <TrendingUp color="secondary" />,
            color: '#dc004e',
            tooltip: 'Percentage of total quota used across all active subscriptions'
        },
        {
            title: 'Content Generated',
            value: usage.content_generation || 0,
            icon: <Person color="success" />,
            color: '#2e7d32',
            tooltip: 'Total content pieces generated across all subscriptions'
        },
        {
            title: 'Images Created',
            value: usage.image_generation || 0,
            icon: <Business color="warning" />,
            color: '#ed6c02',
            tooltip: 'Total images generated across all subscriptions'
        }
    ];

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h4" gutterBottom>
                Welcome back, {user?.first_name || user?.username}!
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}
            {success && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
                    {success}
                </Alert>
            )}

            <Grid container spacing={3}>
                {stats.map((stat, index) => (
                    <Grid item xs={12} sm={6} md={3} key={index}>
                        <Tooltip title={stat.tooltip} arrow>
                            <Card>
                                <CardContent>
                                    <Box display="flex" alignItems="center" justifyContent="space-between">
                                        <Box>
                                            <Typography color="textSecondary" gutterBottom>
                                                {stat.title}
                                            </Typography>
                                            <Typography variant="h4" component="div">
                                                {stat.value}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ color: stat.color }}>
                                            {stat.icon}
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Tooltip>
                    </Grid>
                ))}
            </Grid>

            <Grid container spacing={3} sx={{ mt: 2 }}>
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Usage Overview
                        </Typography>
                        <Box>
                            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                                <Typography variant="body2">Content Generation</Typography>
                                <Typography variant="body2" fontWeight="bold">
                                    {usage.content_generation || 0} / {usage.content_limit || 0}
                                </Typography>
                            </Box>
                            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                                <Typography variant="body2">Image Generation</Typography>
                                <Typography variant="body2" fontWeight="bold">
                                    {usage.image_generation || 0} / {usage.image_limit || 0}
                                </Typography>
                            </Box>
                            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                                <Typography variant="body2">Blog Generation</Typography>
                                <Typography variant="body2" fontWeight="bold">
                                    {usage.blog_generation || 0} / {usage.blog_limit || 0}
                                </Typography>
                            </Box>
                        </Box>
                    </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Quick Actions
                        </Typography>
                        <Box>
                            <Button
                                variant="contained"
                                startIcon={<CreateIcon />}
                                onClick={() => window.location.href = '/subscriptions?new=1'}
                                fullWidth
                                sx={{ mb: 2 }}
                            >
                                Add New Subscription
                            </Button>
                            {hasAnyAWCSubscription && (
                                <Button
                                    variant="outlined"
                                    startIcon={<AutoAwesomeIcon />}
                                    onClick={() => window.location.href = '/service-operations'}
                                    fullWidth
                                    sx={{ mb: 2 }}
                                >
                                    Go to AWC
                                </Button>
                            )}
                            <Button
                                variant="outlined"
                                startIcon={<AnalyticsIcon />}
                                onClick={() => window.location.href = '/cas'}
                                fullWidth
                            >
                                Go to CAS
                            </Button>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>

            {/* Enhanced Subscription List with Individual Controls */}
            <Paper sx={{ mt: 3 }}>
                <Box sx={{ p: 3 }}>
                    <Typography variant="h5" gutterBottom>
                        Subscription Management & Operations
                    </Typography>

                    {Array.isArray(subscriptions) && subscriptions.filter(sub => sub.status === 'active').length > 0 ? (
                        isMobile ? (
                            // Mobile view - Cards
                            <Box>
                                {subscriptions.filter(sub => sub.status === 'active').map((subscription) => (
                                    <SubscriptionCard key={subscription.id} subscription={subscription} />
                                ))}
                            </Box>
                        ) : (
                            // Desktop view - Table
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Subscription</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell>Quota Usage</TableCell>
                                            <TableCell>Content Generated</TableCell>
                                            <TableCell>Images Created</TableCell>
                                            <TableCell>Blogs Published</TableCell>
                                            <TableCell>Pages Created</TableCell>
                                            <TableCell>Last Activity</TableCell>
                                            <TableCell>Quick Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {subscriptions.filter(sub => sub.status === 'active').map((subscription) => (
                                            <TableRow key={subscription.id}>
                                                <TableCell>
                                                    <Box>
                                                        <Typography variant="subtitle1" fontWeight="bold">
                                                            {subscription.subscription_name || subscription.plan?.name || 'Unnamed Subscription'}
                                                        </Typography>
                                                        <Typography variant="body2" color="textSecondary">
                                                            {subscription.subscription_name ?
                                                                `Plan: ${subscription.plan?.name || 'Unknown Plan'} - $${subscription.plan?.price_monthly || 0}/month` :
                                                                `$${subscription.plan?.price_monthly || 0}/month`
                                                            }
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        icon={getStatusIcon(subscription.status)}
                                                        label={subscription.status}
                                                        color={getStatusColor(subscription.status)}
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    {isCASOnlySubscription(subscription) ? (
                                                        <Box>
                                                            <Typography variant="body2" color="textSecondary">
                                                                CAS Access Only
                                                            </Typography>
                                                            <Typography variant="caption" color="textSecondary">
                                                                No quota limits
                                                            </Typography>
                                                        </Box>
                                                    ) : (
                                                        <Box>
                                                            <Typography variant="body2">
                                                                {subscription.quota_used || 0} / {subscription.quota_limit || 0}
                                                            </Typography>
                                                            <Typography variant="caption" color="textSecondary">
                                                                {subscription.quota_remaining || 0} remaining
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {isCASOnlySubscription(subscription) ? (
                                                        <Box>
                                                            <Typography variant="body2" color="textSecondary">
                                                                CAS Content
                                                            </Typography>
                                                            <Typography variant="caption" color="textSecondary">
                                                                Generated via CAS
                                                            </Typography>
                                                        </Box>
                                                    ) : (
                                                        <Box>
                                                            <Typography variant="body2" fontWeight="bold">
                                                                {subscription.content_stats?.total_content || 0} pieces
                                                            </Typography>
                                                            <Typography variant="caption" color="textSecondary">
                                                                {subscription.content_stats?.this_month || 0} this month
                                                            </Typography>
                                                            <Typography variant="caption" color="textSecondary" display="block">
                                                                Last: {subscription.content_stats?.last_generated ? new Date(subscription.content_stats.last_generated).toLocaleDateString() : 'Never'}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {isCASOnlySubscription(subscription) ? (
                                                        <Box>
                                                            <Typography variant="body2" color="textSecondary">
                                                                CAS Images
                                                            </Typography>
                                                            <Typography variant="caption" color="textSecondary">
                                                                Generated via CAS
                                                            </Typography>
                                                        </Box>
                                                    ) : (
                                                        <Box>
                                                            <Typography variant="body2" fontWeight="bold">
                                                                {subscription.image_stats?.total_images || 0} images
                                                            </Typography>
                                                            <Typography variant="caption" color="textSecondary">
                                                                {subscription.image_stats?.this_month || 0} this month
                                                            </Typography>
                                                            <Typography variant="caption" color="textSecondary" display="block">
                                                                Last: {subscription.image_stats?.last_generated ? new Date(subscription.image_stats.last_generated).toLocaleDateString() : 'Never'}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {isCASOnlySubscription(subscription) ? (
                                                        <Box>
                                                            <Typography variant="body2" color="textSecondary">
                                                                CAS Blogs
                                                            </Typography>
                                                            <Typography variant="caption" color="textSecondary">
                                                                Generated via CAS
                                                            </Typography>
                                                        </Box>
                                                    ) : (
                                                        <Box>
                                                            <Typography variant="body2" fontWeight="bold">
                                                                {subscription.blog_stats?.total_blogs || 0} blogs
                                                            </Typography>
                                                            <Typography variant="caption" color="textSecondary">
                                                                {subscription.blog_stats?.this_month || 0} this month
                                                            </Typography>
                                                            <Typography variant="caption" color="textSecondary" display="block">
                                                                Last: {subscription.blog_stats?.last_generated ? new Date(subscription.blog_stats.last_generated).toLocaleDateString() : 'Never'}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {isCASOnlySubscription(subscription) ? (
                                                        <Box>
                                                            <Typography variant="body2" color="textSecondary">
                                                                CAS Pages
                                                            </Typography>
                                                            <Typography variant="caption" color="textSecondary">
                                                                Generated via CAS
                                                            </Typography>
                                                        </Box>
                                                    ) : (
                                                        <Box>
                                                            <Typography variant="body2" fontWeight="bold">
                                                                {subscription.page_stats?.total_pages || 0} pages
                                                            </Typography>
                                                            <Typography variant="caption" color="textSecondary">
                                                                {subscription.page_stats?.this_month || 0} this month
                                                            </Typography>
                                                            <Typography variant="caption" color="textSecondary" display="block">
                                                                Last: {subscription.page_stats?.last_generated ? new Date(subscription.page_stats.last_generated).toLocaleDateString() : 'Never'}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {isCASOnlySubscription(subscription) ? (
                                                        <Box>
                                                            <Typography variant="body2" color="textSecondary">
                                                                CAS Activity
                                                            </Typography>
                                                            <Typography variant="caption" color="textSecondary">
                                                                Check CAS dashboard
                                                            </Typography>
                                                        </Box>
                                                    ) : (
                                                        <Box>
                                                            <Typography variant="body2" fontWeight="bold">
                                                                {subscription.last_activity?.operation ? subscription.last_activity.operation.replace('generate_', '').replace('_', ' ') : 'None'}
                                                            </Typography>
                                                            <Typography variant="caption" color="textSecondary">
                                                                {subscription.last_activity?.timestamp ? new Date(subscription.last_activity.timestamp).toLocaleDateString() : 'Never'}
                                                            </Typography>
                                                            <Typography variant="caption" color="textSecondary" display="block">
                                                                {subscription.last_activity?.details || ''}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <Box display="flex" gap={1.5} flexWrap="wrap" alignItems="center">
                                                        {/* CAS Action - Content Analysis Service */}
                                                        <Tooltip title="Content Analysis Service (AI-driven content generation with analysis)">
                                                            <IconButton
                                                                size="medium"
                                                                sx={{
                                                                    backgroundColor: 'secondary.light',
                                                                    color: 'secondary.contrastText',
                                                                    '&:hover': {
                                                                        backgroundColor: 'secondary.main',
                                                                    }
                                                                }}
                                                                onClick={() => navigate(`/cas?subscriptionId=${subscription.id}`)}
                                                            >
                                                                <AnalyticsIcon />
                                                            </IconButton>
                                                        </Tooltip>

                                                        {/* AWC Action - Automated Website Creation */}
                                                        {!isCASOnlySubscription(subscription) && (
                                                            <Tooltip title="Automated Website Creation (User and AI driven content generation)">
                                                                <IconButton
                                                                    size="medium"
                                                                    sx={{
                                                                        backgroundColor: 'info.light',
                                                                        color: 'info.contrastText',
                                                                        '&:hover': {
                                                                            backgroundColor: 'info.main',
                                                                        }
                                                                    }}
                                                                    onClick={() => navigate(`/service-operations?subscriptionId=${subscription.id}`)}
                                                                >
                                                                    <AutoAwesomeIcon />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )
                    ) : (
                        <Box textAlign="center" py={4}>
                            <Typography variant="body1" color="textSecondary">
                                No active subscriptions found. Create your first subscription to get started.
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Paper>

            {/* Operation Dialog */}
            <Dialog
                open={operationDialog}
                onClose={() => setOperationDialog(false)}
                maxWidth="md"
                fullWidth
                fullScreen={isMobile}
            >
                <DialogTitle>
                    Trigger {selectedOperation === 'images' ? 'Image Generation' :
                        selectedOperation === 'blogs' ? 'Blog Generation' :
                            selectedOperation === 'pages' ? 'Page Generation' : 'Website Creation'}
                    {selectedSubscription && (
                        <Typography variant="subtitle2" color="textSecondary">
                            Subscription: {selectedSubscription.subscription_name || selectedSubscription.plan?.name || 'Unnamed Subscription'}
                        </Typography>
                    )}
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel>Operation Type</InputLabel>
                                <Select
                                    value={selectedOperation}
                                    disabled
                                    label="Operation Type"
                                >
                                    <MenuItem value="images">Generate Images</MenuItem>
                                    <MenuItem value="blogs">Generate Blogs</MenuItem>
                                    <MenuItem value="pages">Generate Pages</MenuItem>
                                    <MenuItem value="website">Create Website</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        {selectedOperation === 'pages' && (
                            <>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Requested Pages (comma-separated)"
                                        placeholder="About, Services, Contact, etc."
                                        value={operationForm.requested_pages}
                                        onChange={(e) => setOperationForm({ ...operationForm, requested_pages: e.target.value })}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={operationForm.include_default_pages}
                                                onChange={(e) => setOperationForm({ ...operationForm, include_default_pages: e.target.checked })}
                                            />
                                        }
                                        label="Include Default Pages"
                                    />
                                </Grid>
                            </>
                        )}

                        {selectedOperation === 'blogs' && (
                            <>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={3}
                                        label="Blog Topics (one per line)"
                                        placeholder="Industry trends and insights&#10;How-to guides and tutorials&#10;Best practices and case studies"
                                        value={operationForm.requested_blog_topics}
                                        onChange={(e) => setOperationForm({ ...operationForm, requested_blog_topics: e.target.value })}
                                        helperText="Enter one topic per line"
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={operationForm.generate_blogs}
                                                onChange={(e) => setOperationForm({ ...operationForm, generate_blogs: e.target.checked })}
                                            />
                                        }
                                        label="Generate Blogs"
                                    />
                                </Grid>
                            </>
                        )}

                        <Grid item xs={12} md={6}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={operationForm.image_generation_required}
                                        onChange={(e) => setOperationForm({ ...operationForm, image_generation_required: e.target.checked })}
                                    />
                                }
                                label="Image Generation Required"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={operationForm.use_default_placeholder_images}
                                        onChange={(e) => setOperationForm({ ...operationForm, use_default_placeholder_images: e.target.checked })}
                                    />
                                }
                                label="Use Default Placeholder Images"
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOperationDialog(false)}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleTriggerOperation}
                        variant="contained"
                        disabled={executing}
                        startIcon={executing ? <CircularProgress size={20} /> : <ExecuteIcon />}
                    >
                        {executing ? 'Executing...' : 'Trigger Operation'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default DashboardPage; 